import { useState } from "react";
import { toast } from "sonner";
import { History, RotateCcw, Eye, X, Loader2 } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import { StatusBadge } from "./AFields";

export function VersionHistory({ coll, itemId, onRestored, hasDraft = false }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const r = await api.get(`/admin/versions/${coll}/${itemId}`);
      setItems(r.data);
      setOpen(true);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const restore = async (v) => {
    const warning = hasDraft
      ? "You already have unpublished changes. Restoring this version will REPLACE the current draft and keep it as a DRAFT. Continue?"
      : `Restore the version from ${String(v.at).replace("T", " ").slice(0, 19)}? It will come back as a DRAFT — the live website will not change until you publish.`;
    if (!window.confirm(warning)) return;
    setBusy(v.id);
    try {
      await api.post(`/admin/versions/${v.id}/restore`);
      toast.success("Version restored as draft — review and publish when ready");
      setOpen(false);
      onRestored?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  return (
    <>
      <button onClick={load} className="rounded-md border border-gray-200 p-2 text-navy transition-colors hover:border-saffron-500"
        aria-label="Version history" title="Version history" data-testid={`history-${itemId}`}><History size={14} /></button>
      {open && (
        <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-navy/60 p-4 backdrop-blur-sm" data-testid="versions-modal">
          <div className="my-8 w-full max-w-2xl rounded-xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-bold text-navy">Version History</h2>
              <button onClick={() => { setOpen(false); setView(null); }} className="rounded-full p-1.5 text-slate-400 hover:bg-gray-100" aria-label="Close" data-testid="versions-close"><X size={18} /></button>
            </div>
            <p className="mt-1 text-xs text-slate-500">Last 30 versions. Restoring always brings the version back as a draft — never directly live.</p>
            <div className="mt-4 max-h-[60vh] space-y-2 overflow-y-auto">
              {items.map((v) => (
                <div key={v.id} className="flex items-center justify-between gap-3 rounded-md border border-gray-100 p-3" data-testid={`version-${v.id}`}>
                  <div>
                    <p className="text-sm font-bold text-navy">{String(v.at || "").replace("T", " ").slice(0, 19)}</p>
                    <p className="text-xs text-slate-500">by {v.by} {v.status && <>· <StatusBadge value={v.status} /></>}</p>
                  </div>
                  <div className="flex gap-1.5">
                    <button onClick={() => setView(view?.id === v.id ? null : v)} className="rounded-md border border-gray-200 p-1.5 text-navy hover:border-saffron-500" aria-label="View" data-testid={`version-view-${v.id}`}><Eye size={13} /></button>
                    <button onClick={() => restore(v)} disabled={busy === v.id} className="rounded-md bg-navy px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50" data-testid={`version-restore-${v.id}`}>
                      {busy === v.id ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} className="mr-1 inline" />} Restore
                    </button>
                  </div>
                </div>
              ))}
              {items.length === 0 && <p className="py-8 text-center text-sm text-slate-400">No previous versions yet — versions are recorded on every update/publish.</p>}
            </div>
            {view && (
              <pre className="mt-4 max-h-48 overflow-auto rounded-md bg-navy-50 p-3 text-[11px] text-slate-600" data-testid="version-preview">{JSON.stringify(view.data, null, 1).slice(0, 4000)}</pre>
            )}
          </div>
        </div>
      )}
    </>
  );
}
